import { Component, h, Prop, State, Event, type EventEmitter, Listen, Watch } from '@stencil/core';
import { makeLogger } from '../../../logger/logger';
import { _getToken } from '../../guided/subcomponents/utils';
import { translations } from './translations';

const { info, warn, err } = makeLogger({
  pathToCurrentFile: 'components/widgets/product-recommendation/product-recommendation-widget.tsx',
  loggersList: ['info', 'warn', 'err']
});

@Component({
  tag: 'uiaas-search-widget-product-recommendation',
  styleUrl: 'product-recommendation.scss',
  shadow: true
})
export class WidgetProductRecommendation {
  /**
   * Event emitted when the product recommendation widget is clicked.
   */
  @Event({
    eventName: 'search:productRecommendationWidgetClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  })
    eventEmitterProductRecommendationWidgetClicked: EventEmitter;

  /**
   * Event emitted when the product recommendation widget is shown.
   */
  @Event({
    eventName: 'search:productRecommendationWidgetShow',
    composed: true,
    cancelable: true,
    bubbles: true
  })
    eventEmitterProductRecommendationWidgetShow: EventEmitter;
  /**
     * Country code, used to retrieve data from config file
     */
  @Prop() readonly country: string;

  /**
   * Language name, used to retrieve data from config file
   */
  @Prop() readonly language: string;

  /**
   * Search term, used to retrieve data from config file
   */
  // eslint-disable-next-line @stencil-community/strict-mutable
  @Prop({ mutable: true, reflect: true }) term: string;

  @Prop() readonly apiEndpoint: string;
  @Prop() readonly apigeeAuth: string;
  @Prop() readonly apigeeTokenUrl: string;
  @Prop({ mutable: true, reflect: true }) bearerToken: string;
  @Prop() readonly isSidepanel: boolean;
  @Prop() readonly isRecommendationCallback?: (products: Array<object>) => void;

  @State() products: Array<object> = [];

  @State() categoryList: string[] = [];

  @State() selectedCategory: string;

  @State() isMobileView: boolean;

  @State() isDragging: boolean = false;

  @State() startX: number;

  @State() scrollLeft: number;

  private categoryListRef: HTMLElement;

  @Listen('search:started', { target: 'window' })
  searchStartedHandler(e) {
      this.term = e.detail.term;
  }

  @Watch('term')
  termChanged(newValue: string, oldValue: string) {
      if (newValue !== oldValue) {
        this.fetchProducts();
      }
  }

  componentWillLoad() {
      this.fetchProducts();
      this.isMobileView = this.isSidepanel ? window.innerWidth <= 781 : window.innerWidth <= 540;
  }

  private async fetchProducts() {
      if (!this.term) {
        return;
      }

      const result = await _getToken({tokenUrl: this.apigeeTokenUrl, bearer: this.apigeeAuth});
      this.bearerToken = result.access_token;
      const myHeaders = new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.bearerToken}`
      });

      const raw = JSON.stringify({
        'searcher': 'se/products/recommendation',
        'context': {
          'type': 'cose/context/locale',
          'country': this.country,
          'language': this.language
        },
        'term': this.term,
        'forceTerm': true
      });

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: raw
      };

      try {
        const response = await fetch(
          this.apiEndpoint.replace('v1', 'v2'),
          requestOptions
        );
        const result = await response.json();
        this.products = result.documents;
        this.updateCategoryList();
        this.selectedCategory = this.categoryList[0];
      } catch (error) {
        err(`unable to get recomended products ${error}`);
        this.products = [];
      }

      if (this.isSidepanel) {
        this.isRecommendationCallback(this.products);
      }
  }

  private updateCategoryList() {
      const categories = new Set<string>();
      this.products.forEach(product => {
          categories.add(product['relationshipsCategory']);
      });
      this.categoryList = Array.from(categories);
  }

  private getCategoryProductCount(category: string): number {
      return this.products.filter(product => product['relationshipsCategory'] === category).length;
  }

  private handleCategoryClick(category: string) {
      if (this.selectedCategory === category) {
        this.selectedCategory = null;
      } else {
        this.selectedCategory = category;
      }
  }

  private onClickPublisher(e, category: string, url: string, index: number, button): void {
      this.eventEmitterProductRecommendationWidgetClicked.emit({
        term: this.term,
        link: url,
        button: button,
        category: category,
        position: index
      });
  }

  private productCard = (product, index, locale, isMobile?, isSidepanel?) => {
      return (
        <div class="product-card">
          {isMobile && isSidepanel ? 
            <qds-standalone-link
              href={product[locale]?.openUrl}
              onMouseDown={(e) => this.onClickPublisher(e, product.relationshipsCategory, product[locale]?.openUrl, index, e.button !== 0 ? 'right' : 'left')}
              onKeyDown={(e) => e.key === 'Enter' && this.onClickPublisher(e, product.relationshipsCategory, product[locale]?.openUrl, index, e.key)}
            >
              <span>
                <img src={product.image}/>
                <span>
                  <p>{product.id}</p>
                  <span>{product[locale]?.title}</span>
                </span>
              </span>
            </qds-standalone-link> : 
            <qds-standalone-link
              href={product[locale]?.openUrl}
              onMouseDown={(e) => this.onClickPublisher(e, product.relationshipsCategory, product[locale]?.openUrl, index, e.button !== 0 ? 'right' : 'left')}
              onKeyDown={(e) => e.key === 'Enter' && this.onClickPublisher(e, product.relationshipsCategory, product[locale]?.openUrl, index, e.key)}
            >
              <img src={product.image}/>
              <p>{product.id}</p>
              <span>{product[locale]?.title}</span>
            </qds-standalone-link>
          }           
        </div>
      );
  };

  private handleMouseDown(e: MouseEvent | TouchEvent) {
      this.isDragging = true;
      const pageX = e instanceof MouseEvent ? e.pageX : e.touches[0].pageX;
      this.startX = pageX - this.categoryListRef.offsetLeft;
      this.scrollLeft = this.categoryListRef.scrollLeft;
  }

  private handleMouseLeave() {
      this.isDragging = false;
  }

  private handleMouseUp() {
      this.isDragging = false;
  }

  private handleMouseMove(e: MouseEvent | TouchEvent) {
      if (!this.isDragging) return;
      e.preventDefault();
      const pageX = e instanceof MouseEvent ? e.pageX : e.touches[0].pageX;
      const x = pageX - this.categoryListRef.offsetLeft;
      const walk = (x - this.startX);
      this.categoryListRef.scrollLeft = this.scrollLeft - walk;
  }
  
  render() {
      const locale = `${this.language}_${this.country?.toUpperCase()}`;

      const filteredProducts = this.selectedCategory
        ? this.products?.filter(product => product['relationshipsCategory'] === this.selectedCategory)
        : this.products;

      if (this.products?.length > 0) {
        const uiaasSearchElement = document.querySelector('uiaas-search');

        if (uiaasSearchElement) {
          const observer = new MutationObserver((mutationsList) => {
              mutationsList.forEach(({ type, attributeName }) => {
                  if (type === 'attributes' && attributeName === 'active-tab') {
                    const activeTab = uiaasSearchElement.getAttribute('active-tab');
                    if (activeTab === '1') {
                      this.eventEmitterProductRecommendationWidgetShow.emit({term: this.term});
                      observer.disconnect();
                    }
                  }
              });
          });

          observer.observe(uiaasSearchElement, { attributes: true });
        }

        return (
          <div class={`widget-container${this.isSidepanel ? '__sidepanel' : ''}`}>
            {(!this.isSidepanel &&
            <p>
              <b>{this.term}</b> {translations.isCompatibleWith[this.language]}
            </p>
            )}
            <div
              class={`category-list${this.isSidepanel ? '__sidepanel' : ''} ${this.isMobileView && this.isSidepanel ? 'sticky' : ''}`}
              ref={(el) => (this.categoryListRef = el as HTMLElement)}
              onMouseDown={(e) => this.handleMouseDown(e)}
              onTouchStart={(e) => this.handleMouseDown(e)}
              onMouseLeave={() => this.handleMouseLeave()}
              onMouseUp={() => this.handleMouseUp()}
              onTouchEnd={() => this.handleMouseUp()}
              onMouseMove={(e) => this.handleMouseMove(e)}
              onTouchMove={(e) => this.handleMouseMove(e)}
            >
              {this.categoryList?.map((category) => (
                <qds-chip
                  text={`${category} (${this.getCategoryProductCount(category)})`}
                  onClick={() => this.handleCategoryClick(category)}
                  checked={category === this.selectedCategory}
                />
              ))}
            </div>
            {(filteredProducts.length > 3 && !this.isMobileView) || (filteredProducts.length > 2 && (this.isMobileView || this.isSidepanel)) && !(this.isMobileView && this.isSidepanel) ? (
              <se-carousel pagination="true">
                {filteredProducts.map((product, index) => (
                  <se-carousel-item>
                    {this.productCard(product, index, locale)}
                  </se-carousel-item>
                ))}
              </se-carousel>) : (
              <div class={`product-card-list${this.isSidepanel && this.isMobileView ? '__sidepanel-mobile' : ''}`}>
                {filteredProducts.map((product, index) => (
                  this.productCard(product, index, locale, this.isMobileView, this.isSidepanel)
                ))}
              </div>
            )} 
          </div>
        );
      }
  }
}
